import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import ControlledRadioButtonGroup from 'src/shared/components/form/controlledRadioButtonGroup';
import * as yup from 'yup';
import { Schema } from 'yup';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, Grid, CircularProgress } from '@mui/material';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { HeaderSubTitle } from 'src/modules/dashboard/components/atoms/headerSubTitle';
import { createCustomer } from 'src/shared/services/installCustomerService';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { InstallCustomerRequest } from '../models/installCustomerRequest';

const InstallCustomerForm = () => {
  const { t } = useTranslation(['common', 'installCustomer']);
  const isMounted = useIsMounted();
  const requiredMessage = t('common:validationMessages.required');

  const [loading, setLoading] = useState(false);

  const InstallCustomerRequestPostSchema: Schema<InstallCustomerRequest> = yup.object().shape({
    countryAssociationStructure: yup.number().required(requiredMessage),
    trustAccount: yup.number().required(requiredMessage),
    centralConfig: yup.number().required(requiredMessage),
    ocrSetting: yup.number().required(requiredMessage),
    countryName: yup.string().required(requiredMessage),
    dbLoginName: yup.string().required(requiredMessage),
    dbUserName: yup.string().required(requiredMessage),
    userEmail: yup.string().email().required(requiredMessage),
    userFirstName: yup.string().required(requiredMessage),
    userLastName: yup.string().required(requiredMessage),
    userName: yup.string().required(requiredMessage),
    userPassword: yup.string().required(requiredMessage),
    privateName: yup.string().required(requiredMessage),
    privateCO: yup.string().required(requiredMessage),
    privateAddress: yup.string().required(requiredMessage),
    privateZip: yup.string().required(requiredMessage),
    privateCity: yup.string().required(requiredMessage),
    privateCountry: yup.string().required(requiredMessage),
    privatePhone: yup.string().required(requiredMessage),
    privateEmail: yup.string().email().required(requiredMessage),
    privateEmailPaymentsNotifications: yup.string().email().required(requiredMessage),
    privateEmailSendingsNotifications: yup.string().email().required(requiredMessage),
    publicCO: yup.string().required(requiredMessage),
    publicAddress: yup.string().required(requiredMessage),
    publicZip: yup.string().required(requiredMessage),
    publicCity: yup.string().required(requiredMessage),
    publicCountry: yup.string().required(requiredMessage),
    publicPhone: yup.string().required(requiredMessage),
    publicEmail: yup.string().email().required(requiredMessage),
    publicOrgNo: yup.string().required(requiredMessage),
    publicPaymentsAccount: yup.string().required(requiredMessage),
    publicWebsite: yup.string().required(requiredMessage),
    publicExpireDate: yup.string().required(requiredMessage),
    invAccountNo: yup.string().required(requiredMessage),
  });

  const formMethods = useForm({
    resolver: yupResolver(InstallCustomerRequestPostSchema),
    defaultValues: {
      dbServerName: 'fsweb2',
      memberNumberType: '',
      createDataMreg: false,
      defaultDb: 'fs-medlem',
      style: 'fs',
      districtLabel: 'Län',
      localLabel: 'Lokalförening',
      systemName: 'Föreningssupport.se',
      countryAssociationStructure: 0,
      trustAccount: 0,
      centralConfig: 0,
      ocrSetting: 0,
      countryName: '',
      dbLoginName: '',
      dbUserName: '',
      userEmail: '',
      userFirstName: '',
      userLastName: '',
      userName: '',
      userPassword: '',
      privateName: '',
      privateCO: '',
      privateAddress: '',
      privateZip: '',
      privateCity: '',
      privateCountry: '',
      privatePhone: '',
      privateEmail: '',
      privateEmailPaymentsNotifications: '',
      privateEmailSendingsNotifications: '',
      privatePaymentsAccount: '',
      publicCO: '',
      publicAddress: '',
      publicZip: '',
      publicCity: '',
      publicCountry: '',
      publicPhone: '',
      publicEmail: '',
      publicEmailResposible: '',
      publicOrgNo: '',
      publicPaymentsAccount: '',
      publicWebsite: '',
      publicExpireDate: '',
      publicGracePeriod: '',
      invAccountNo: '',
      invBIC: '',
      invIBAN: '',
    },
  });
  const { handleSubmit } = formMethods;

  const onSubmit = async (data: InstallCustomerRequest) => {
    setLoading(true);
    try {
      const res = await createCustomer(data);
      if (isMounted()) {
        ConsoleHelper.log('RES:: ', res.data);
      }
    } catch (error) {
      if (isMounted()) {
        ConsoleHelper.log(error);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress role='alert' aria-busy='true' aria-label={t('common:statusMessages.loading')} />
            </Box>
          )}
          {!loading && (
            <GridWrapper>
              <Grid item xs={12}>
                <HeaderSubTitle label={t('common:sideMenu.organization')} />
              </Grid>
              <Grid item xs={12}>
                <ControlledRadioButtonGroup
                  name='countryAssociationStructure'
                  defaultValue={0}
                  required
                  label='Organisationsstruktur'
                  items={[
                    {
                      value: 0,
                      label: 'Enskild Förening',
                    },
                    {
                      value: 1,
                      label: 'Förbund + Föreningar',
                    },
                    {
                      value: 2,
                      label: 'Förbund, Distrikt och Föreningar',
                    },
                  ]}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <ControlledRadioButtonGroup
                  name='trustAccount'
                  defaultValue={0}
                  required
                  label='Klientmedelskund'
                  items={[
                    {
                      value: 0,
                      label: 'Nej',
                    },
                    {
                      value: 1,
                      label: 'Ja',
                    },
                  ]}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <ControlledRadioButtonGroup
                  name='ocrSetting'
                  defaultValue={0}
                  required
                  label='Central inkassering'
                  items={[
                    {
                      value: 0,
                      label: 'Nej',
                    },
                    {
                      value: 1,
                      label: 'Ja',
                    },
                  ]}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <ControlledRadioButtonGroup
                  name='centralConfig'
                  defaultValue={0}
                  required
                  label='Förbundsgemensam avgift'
                  items={[
                    {
                      value: 0,
                      label: 'Nej',
                    },
                    {
                      value: 1,
                      label: 'Ja',
                    },
                  ]}
                />
              </Grid>

              <Grid item xs={12}>
                <ControlledTextField
                  name='countryName'
                  // required - blir fult, yup hanterar det istället?
                  placeholder='Förbundets/Organisationens namn'
                  label={t('installCustomer:formFields.countryName')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField
                  name='dbLoginName'
                  // required
                  placeholder={'Kortnamn - EJ ÅÄÖ - ex: "vallas" för Vallås Villaförening'}
                  label={t('installCustomer:formFields.dbLoginName')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField
                  name='dbUserName'
                  // required
                  placeholder={'Kortnamn - EJ ÅÄÖ - ex: "vallas" för Vallås Villaförening'}
                  label={t('installCustomer:formFields.dbUserName')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <HeaderSubTitle label='Användare' />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  name='userEmail'
                  // required
                  label={t('installCustomer:formFields.userEmail')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField
                  name='userFirstName'
                  // required
                  label={t('installCustomer:formFields.userFirstName')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField
                  name='userLastName'
                  // required
                  label={t('installCustomer:formFields.userLastName')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField
                  name='userName'
                  // required
                  placeholder='Ex. FöEf (Förnamn Efternamn)'
                  label={t('installCustomer:formFields.userName')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField
                  name='userPassword'
                  // required
                  placeholder='Ex. samma som kortnamn'
                  label={t('installCustomer:formFields.userPassword')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <HeaderSubTitle label='Kund för fakturering' />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  name='privateName'
                  // required
                  placeholder='Vår beställare'
                  label={t('installCustomer:formFields.privateName')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField
                  name='privateCO'
                  // required
                  placeholder='Kundens betalningsansvarige'
                  label={t('installCustomer:formFields.privateCO')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField
                  name='privateAddress'
                  // required
                  label={t('installCustomer:formFields.privateAddress')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <ControlledTextField
                  name='privateZip'
                  // required
                  label={t('installCustomer:formFields.privateZip')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <ControlledTextField
                  name='privateCity'
                  // required
                  label={t('installCustomer:formFields.privateCity')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <ControlledTextField
                  name='privateCountry'
                  // required
                  label={t('installCustomer:formFields.privateCountry')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField
                  name='privatePhone'
                  // required
                  label={t('installCustomer:formFields.privatePhone')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField
                  name='privateEmail'
                  // required
                  label={t('installCustomer:formFields.privateEmail')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField
                  name='privateEmailPaymentsNotifications'
                  // required
                  label={t('installCustomer:formFields.privateEmailPaymentsNotifications')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField
                  name='privateEmailSendingsNotifications'
                  // required
                  label={t('installCustomer:formFields.privateEmailSendingsNotifications')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  name='privatePaymentsAccount'
                  placeholder='000-0000'
                  label={t('installCustomer:formFields.privatePaymentsAccount')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <HeaderSubTitle label='Organisationens publika uppgifter (syns på ex: faktura)' />
              </Grid>

              <Grid item xs={6}>
                <ControlledTextField
                  name='publicCO'
                  // required
                  label={t('installCustomer:formFields.publicCO')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField
                  name='publicAddress'
                  // required
                  label={t('installCustomer:formFields.publicAddress')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <ControlledTextField
                  name='publicZip'
                  // required
                  label={t('installCustomer:formFields.publicZip')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <ControlledTextField
                  name='publicCity'
                  // required
                  label={t('installCustomer:formFields.publicCity')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <ControlledTextField
                  name='publicCountry'
                  // required
                  label={t('installCustomer:formFields.publicCountry')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField
                  name='publicPhone'
                  // required
                  placeholder='000-0000000'
                  label={t('installCustomer:formFields.publicPhone')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField
                  name='publicEmail'
                  // required
                  label={t('installCustomer:formFields.publicEmail')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  name='publicEmailResposible'
                  placeholder='Sätts bara om organisationsstruktur är Enskild förening'
                  label={t('installCustomer:formFields.publicEmailResposible')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={4} lg={2}>
                <ControlledTextField
                  name='publicOrgNo'
                  // required
                  label={t('installCustomer:formFields.publicOrgNo')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={8} lg={10}>
                <ControlledTextField
                  name='publicPaymentsAccount'
                  // required
                  placeholder='Viktigt om klientmedelskund. Skrivs ut på faktura. Bör i de allra flesta fall vara samma som invaccountno'
                  label={t('installCustomer:formFields.publicPaymentsAccount')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <ControlledTextField
                  name='publicWebsite'
                  // required
                  label={t('installCustomer:formFields.publicWebsite')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField
                  name='publicExpireDate'
                  // required
                  label={t('installCustomer:formFields.publicExpireDate')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField
                  name='publicGracePeriod'
                  label={t('installCustomer:formFields.publicGracePeriod')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <ControlledTextField
                  name='invAccountNo'
                  // required
                  placeholder='Viktigt om klientmedelskund. Sätts på fakturagrupp.'
                  label={t('installCustomer:formFields.invAccountNo')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={3}>
                <ControlledTextField
                  name='invBIC'
                  label={t('installCustomer:formFields.invBIC')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={3}>
                <ControlledTextField
                  name='invIBAN'
                  label={t('installCustomer:formFields.invIBAN')}
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button size='large' variant='contained' fullWidth color='success' type='submit' disableElevation disabled={loading}>
                  {t('common:button.save')}
                </Button>
              </Grid>
            </GridWrapper>
          )}
        </form>
      </FormProvider>
    </>
  );
};

export default InstallCustomerForm;
